/**
 * **Pinch to zoom the phone activity map's board** (mobile web; user decision 2026-11-03, with iOS/Android
 * `ActivityMapScreen`). Pure: the map (`Home3PhoneMap`) feeds it the fingers and writes the result as a CSS transform
 * on the board's sizer — `translate(x, y) scale(k)` with the origin at the sizer's top-left — so nothing is laid out
 * again and the flow's sim never rebuilds while a pinch runs.
 *
 * - 1× is the default and the floor; `maxZoom` the ceiling. There is no double-tap.
 * - A pinch is anchored at the fingers' centroid: the board point under the centroid when the pinch began stays under
 *   it as the fingers spread and move.
 * - While zoomed, a one-finger drag pans. The pan is clamped so the board's edges never pull inside the band: the
 *   scaled board always covers it. At 1× that leaves exactly no pan, so pinching back to 1× resets it.
 */

/** The board's zoom: its scale, and its top-left's offset in band pixels. */
export interface PhoneZoom {
  k: number;
  x: number;
  y: number;
}

/** The ceiling of a pinch; 1× is the floor. */
/** The zoom's ceiling: 2× (the user's call, 2026-11-03; it was 3×). */
export const maxZoom = 2;
/** How far (band pixels) a finger travels before a press is a drag, not a tap — past it, the press's click is eaten. */
export const zoomTapSlop = 6;

export const identityZoom: PhoneZoom = { k: 1, x: 0, y: 0 };

/** The zoom kept inside its range and its pan inside the band (`width` × `height`, band pixels). */
export function clampZoom(z: PhoneZoom, width: number, height: number): PhoneZoom {
  const k = Math.min(maxZoom, Math.max(1, Number.isFinite(z.k) ? z.k : 1));
  // The scaled board spans [x, x + width·k]; it must cover [0, width], so x ∈ [width·(1 − k), 0]. Same for y.
  const x = Math.min(0, Math.max(width * (1 - k), z.x));
  const y = Math.min(0, Math.max(height * (1 - k), z.y));
  // `+ 0` turns a −0 into 0, so a reset compares equal to the identity.
  return { k, x: x + 0, y: y + 0 };
}

/** A point in band pixels. */
export interface ZoomPoint {
  x: number;
  y: number;
}

/**
 * The zoom a pinch has reached: it began at `start` with its fingers' centroid at `from` and `fromSpread` apart, and
 * they are now at `to`, `toSpread` apart. The board point under `from` lands under `to`.
 */
export function pinchZoom(
  start: PhoneZoom,
  from: ZoomPoint,
  fromSpread: number,
  to: ZoomPoint,
  toSpread: number,
  width: number,
  height: number,
): PhoneZoom {
  const k = Math.min(maxZoom, Math.max(1, start.k * (fromSpread > 0 ? toSpread / fromSpread : 1)));
  // The board point (unscaled sizer pixels) under the starting centroid.
  const bx = (from.x - start.x) / start.k;
  const by = (from.y - start.y) / start.k;
  return clampZoom({ k, x: to.x - bx * k, y: to.y - by * k }, width, height);
}

/** A one-finger drag while zoomed: the board follows the finger, inside the band. */
export function panZoom(start: PhoneZoom, dx: number, dy: number, width: number, height: number): PhoneZoom {
  return clampZoom({ k: start.k, x: start.x + dx, y: start.y + dy }, width, height);
}

/** The sizer's CSS transform for a zoom (origin: its top-left). The identity is `none`, so 1× is untouched. */
export function zoomTransform(z: PhoneZoom): string {
  return z.k === 1 && z.x === 0 && z.y === 0 ? 'none' : `translate(${z.x}px, ${z.y}px) scale(${z.k})`;
}
